import {
  EnumSingleSelectFieldV2,
  FieldLabel,
  NumericField,
  fieldTypes,
} from "@ucl/library";
import { FC } from "react";
import classNames from "classnames";
import { SLAUnits } from "@bolt/core";
import { isNil } from "lodash";

const SLAUnitOptions = [
  {
    label: "Day(s)",
    value: SLAUnits.Day,
  },
  {
    label: "Hour(s)",
    value: SLAUnits.Hour,
  },
  {
    label: "Minute(s)",
    value: SLAUnits.Minute,
  },
];

export interface SLAComponentProps
  extends fieldTypes.BaseFieldProps<{
    sla?: number;
    slaUnit?: SLAUnits;
  }> {
  availableSLAUnits?: SLAUnits[];
  showLabel?: boolean;
}

export function sanitizeEnumValue(input: number | string): number {
  if (typeof input === "number") {
    return SLAUnits[input] ? input : 1; // Validate if the number is valid in the enum
  }
  if (typeof input === "string") {
    return SLAUnits[input as keyof typeof SLAUnits] || 1; // Validate and return value
  }
  return 1; // Default case for invalid input
}

export const SLAComponent: FC<SLAComponentProps> = ({
  label,
  value,
  isRequired,
  disabled,
  readOnly,
  onSubmit,
  availableSLAUnits,
  showLabel = true,
}) => {
  const sla = value?.sla;
  const unit = value?.slaUnit;

  const setSLA = (updatedSLA: number) => {
    if (onSubmit) onSubmit({ ...value, sla: updatedSLA });
  };

  const setSLAUnit = (updatedSlaUnit: SLAUnits) => {
    if (onSubmit) onSubmit({ ...value, slaUnit: updatedSlaUnit });
  };

  if (readOnly) {
    return (
      <div
        className={classNames(
          {
            "sla-field": true,
          },
          "sla-field-name-container"
        )}
      >
        {showLabel && <FieldLabel labelName={"SLA"} isRequired={isRequired} />}
        {value?.sla && (
          <span className="field-only">
            {sla} {unit === SLAUnits.Day ? "days" : "hours"}
          </span>
        )}
        {isNil(value?.sla) && (
          <span className="field-only placeholder-text">No SLA</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={classNames({
        "sla-field": true,
        "sla-disabled": disabled ?? false,
      })}
    >
      {showLabel && <FieldLabel labelName={label} isRequired={isRequired} />}
      <div
        className={classNames({
          "sla-field-component": true,
          "has-required-background": isRequired && !sla,
        })}
      >
        <NumericField
          isRequired={isRequired}
          value={sla}
          width="50"
          format="n0"
          disabled={disabled}
          readOnly={disabled}
          showIncrementButtons={false}
          onSubmit={(val) => {
            if (val) {
              setSLA(val);
            }
          }}
        />
        <EnumSingleSelectFieldV2
          hideSearchBox={true}
          key={sanitizeEnumValue(unit as number | string)}
          showClearButton={false}
          type={"EnumSingleSelect"}
          value={sanitizeEnumValue(unit as number | string)}
          optionValues={SLAUnitOptions.filter((op) =>
            availableSLAUnits?.some((sla) => sla === op.value)
          )}
          readOnly={disabled}
          disabled={disabled}
          onSubmit={(val) => {
            if (val) {
              setSLAUnit(val as SLAUnits);
            }
          }}
        />
      </div>
    </div>
  );
};
